// src/services/submitTimeService.ts
// Service for SubmitTime component API calls
// Handles time tracking, layers, projects, types, tasks, phases, and teams

import { apiService } from './api';
import { socketService } from './socketService';
import type {
  LayerItem,
  PhaseItem,
  ProjectItem,
  TypeItem,
  TaskItem,
  TeamResponse,
  CustomFieldResponse,
  SubmitTimeConfiguration,
  TimeTrackingItem,
  TimeEvent,
  GetTasksRequest,
  GetEventsRequest,
  DeleteAndUpdateRequest,
  DeleteReplyRequest,
  ReplyRequest,
} from '@/components/SubmitTime/types';

class SubmitTimeService
{
  // ==================== Configuration ====================

  /**
   * Get submit time configurations
   * Returns configuration for which custom fields are enabled/required
   */
  async getSubmitTimeConfigurations(): Promise<SubmitTimeConfiguration[]>
  {
    try
    {
      const response = await apiService.get<SubmitTimeConfiguration[]>(
        '/ConfigValue/GetSubmitTimeConfigurations',
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting submit time configurations:', error);
      return [];
    }
  }

  // ==================== Custom Fields ====================

  /**
   * Get custom fields by item (CoreTaskId)
   * Returns custom fields grouped by LayerId
   */
  async getCustomFieldsByItemId(
    coreTaskId: number,
    isDeleted: boolean = false,
    projectId?: number,
  ): Promise<CustomFieldResponse[]>
  {
    try
    {
      let url = `/CustomField/GetCustomFieldsByItemId?CoreTaskId=${coreTaskId}&IsDeleted=${isDeleted}`;
      if (projectId !== undefined && projectId !== null)
      {
        url += `&ProjectId=${projectId}`;
      }
      const response = await apiService.get<CustomFieldResponse[]>(url);
      return response;
    }
    catch (error)
    {
      console.error('Error getting custom fields by item:', error);
      return [];
    }
  }

  /**
   * Update custom field value (PUT) — used when CustomFieldValueId already exists
   */
  async updateCustomField(customField: Record<string, any>): Promise<void>
  {
    try
    {
      await apiService.put('/CustomFieldValue/', {
        ...customField,
        SocketConnectionId: socketService.connectionId,
      });
    }
    catch (error)
    {
      console.error('Error updating custom field:', error);
      throw error;
    }
  }

  /**
   * Save new custom field value (POST) — used when CustomFieldValueId is 0 or missing
   */
  async saveCustomField(customField: Record<string, any>): Promise<any>
  {
    try
    {
      const response = await apiService.post('/CustomFieldValue/', {
        ...customField,
        SocketConnectionId: socketService.connectionId,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error saving custom field:', error);
      throw error;
    }
  }

  // ==================== Layers ====================

  /**
   * Get layers for a conversation (uses getpersonlayers endpoint)
   */
  async getLayers(_conversationId?: number): Promise<LayerItem[]>
  {
    try
    {
      const response = await apiService.get<LayerItem[]>('layer/getpersonlayers');
      return response;
    }
    catch (error)
    {
      console.error('Error getting layers:', error);
      return [];
    }
  }

  /**
   * Get all layers for the current person
   */
  async getAllLayers(): Promise<LayerItem[]>
  {
    try
    {
      const response = await apiService.get<LayerItem[]>('layer/getpersonlayers');
      return response;
    }
    catch (error)
    {
      console.error('Error getting all layers:', error);
      return [];
    }
  }

  // ==================== Phases ====================

  /**
   * Get phases by conversation ID
   */
  async getPhasesByConversationId(conversationId: number): Promise<PhaseItem[]>
  {
    try
    {
      const response = await apiService.get<PhaseItem[]>(
        `/Conversation/GetPhasesByConversationId?ConversationId=${conversationId}`,
      );
      return response;
    }
    catch (error)
    {
      console.error('Error getting phases:', error);
      return [];
    }
  }

  // ==================== Projects ====================

  /**
   * Get all user projects
   */
  async getAllProjects(): Promise<ProjectItem[]>
  {
    try
    {
      const response = await apiService.get<ProjectItem[]>('/project/getuserprojects');
      return response;
    }
    catch (error)
    {
      console.error('Error getting all projects:', error);
      return [];
    }
  }

  // ==================== Types ====================

  /**
   * Get all types from menu (filters out ConfigValueID 1 and 15)
   */
  async getAllType(): Promise<TypeItem[]>
  {
    try
    {
      const response = await apiService.get<TypeItem[]>('/menu/getbyperson');
      const filtered = Array.isArray(response)
        ? response.filter(menu => menu.ConfigValueID !== 1 && menu.ConfigValueID !== 15)
        : [];
      return filtered;
    }
    catch (error)
    {
      console.error('Error getting all types:', error);
      return [];
    }
  }

  // ==================== Tasks ====================

  /**
   * Get tasks for a project and type
   */
  async getTasks(request: GetTasksRequest): Promise<TaskItem[]>
  {
    try
    {
      const response = await apiService.post<TaskItem[]>('/TaskTimeTracking/GetTasks', request);
      return response;
    }
    catch (error)
    {
      console.error('Error getting tasks:', error);
      return [];
    }
  }

  // ==================== Teams ====================

  /**
   * Get teams - returns default team, all teams, and submittimeteam flag
   */
  async getTeams(): Promise<TeamResponse>
  {
    try
    {
      const response = await apiService.get<TeamResponse>('/CustomField/GetDefaultTeams');
      return response;
    }
    catch (error)
    {
      console.error('Error getting teams:', error);
      return { defaultteam: null, teams: [], submittimeteam: false };
    }
  }

  // ==================== Time Tracking ====================

  /**
   * Get time tracking events for a date range
   */
  async getEvents(request: GetEventsRequest): Promise<TimeEvent[]>
  {
    try
    {
      const response = await apiService.post<TimeEvent[]>('/TaskTimeTracking/GetEvents', request);
      return response;
    }
    catch (error)
    {
      console.error('Error getting time tracking events:', error);
      return [];
    }
  }

  /**
   * Save new time tracking entry
   */
  async saveTimeTracking(timeTracking: TimeTrackingItem): Promise<any>
  {
    try
    {
      const response = await apiService.post('/TaskTimeTracking', {
        ...timeTracking,
        SocketConnectionId: socketService.connectionId,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error saving time tracking:', error);
      throw error;
    }
  }

  /**
   * Update existing time tracking entry
   */
  async updateTimeTracking(timeTracking: TimeTrackingItem): Promise<any>
  {
    try
    {
      const response = await apiService.put('/TaskTimeTracking', {
        ...timeTracking,
        SocketConnectionId: socketService.connectionId,
      });
      return response;
    }
    catch (error)
    {
      console.error('Error updating time tracking:', error);
      throw error;
    }
  }

  /**
   * Delete time entry by ID
   */
  async deleteTimeEntry(timeTrackingId: number): Promise<void>
  {
    try
    {
      await apiService.delete(`/TaskTimeTracking/${timeTrackingId}`);
    }
    catch (error)
    {
      console.error('Error deleting time entry:', error);
      throw error;
    }
  }

  /**
   * Delete and update time tracking (recalculates remaining/actual)
   */
  async deleteAndUpdate(request: DeleteAndUpdateRequest): Promise<void>
  {
    try
    {
      await apiService.put('/TaskTimeTracking/Delete', {
        ...request,
        SocketConnectionId: socketService.connectionId,
      });
    }
    catch (error)
    {
      console.error('Error in deleteAndUpdate:', error);
      throw error;
    }
  }

  // ==================== Replies ====================

  /**
   * Add a new reply to a conversation
   */
  async addReply(replyRequest: ReplyRequest): Promise<any>
  {
    try
    {
      const url = replyRequest.type === 2 ? '/reply/repost/' : '/reply';
      const response = await apiService.post(url, replyRequest);
      return response;
    }
    catch (error)
    {
      console.error('Error adding reply:', error);
      throw error;
    }
  }

  /**
   * Update an existing reply
   */
  async updateReply(replyRequest: ReplyRequest): Promise<any>
  {
    try
    {
      const response = await apiService.post('/reply/update', replyRequest);
      return response;
    }
    catch (error)
    {
      console.error('Error updating reply:', error);
      throw error;
    }
  }

  /**
   * Delete a reply (soft delete)
   */
  async deleteReply(request: DeleteReplyRequest): Promise<void>
  {
    try
    {
      await apiService.put('/reply/RemoveReply', request);
    }
    catch (error)
    {
      console.error('Error deleting reply:', error);
      // Don't throw - non-critical
    }
  }

  // ==================== Conversations ====================

  /**
   * Set a conversation as viewed
   */
  async setConversationsAsViewed(conversationId: number): Promise<void>
  {
    try
    {
      await apiService.put(
        `/alert/SetConversationsAsViewed?conversationIdsString=${conversationId}`,
      );
    }
    catch (error)
    {
      console.error('Error setting conversation as viewed:', error);
      // Don't throw - non-critical
    }
  }
}

// Export singleton instance
export const submitTimeService = new SubmitTimeService();

// Also export class for custom instances if needed
export default SubmitTimeService;
